import { createSlice } from "@reduxjs/toolkit";
import Resizer from "react-image-file-resizer";
import {
  deleteEfficeIcon,
  newRootMaker,
  uploadEfficeIcon,
} from "slices/efficeTreeSlice";
import {
  MAX_EFFICE_ICON_SIDE_LENGTH,
  MIN_EFFICE_ICON_SIDE_LENGTH,
  MAX_EFFICE_ICON_SIZE,
  INVITE_LINK_START_V01,
} from "constants/constants";

export const slice = createSlice({
  name: "homeStart",
  initialState: {
    newRootModal: false,
    editRootError: null,
    editRootId: null,
    tryingEditRoot: false,
    linkPage: false,
    newlyMadeRoot: null,
    rootName: "",
    rootIcon: null,
    rootLink: "",
    rootIconChanged: false,
    rootNameChanged: false,
  },
  reducers: {
    setRootName: (state, action) => {
      state.rootName = action.payload;
    },
    setRootIcon: (state, action) => {
      state.rootIcon = action.payload;
      state.tryingEditRoot = false;
      state.editRootError = null;
    },
    setRootIconChanged: (state, action) => {
      state.rootIconChanged = action.payload;
    },
    setEditRootError: (state, action) => {
      state.editRootError = action.payload;
      state.tryingEditRoot = false;
    },
    setTryingEditRoot: (state, action) => {
      if (action.payload) {
        state.tryingEditRoot = action.payload;
        state.editRootError = null;
      } else {
        state.tryingEditRoot = action.payload;
      }
    },
    setLinkPage: (state, action) => {
      state.linkPage = action.payload;
    },
    setNewlyMadeRoot: (state, action) => {
      state.newlyMadeRoot = action.payload;
    },
    setRootLink: (state, action) => {
      state.rootLink = INVITE_LINK_START_V01 + action.payload;
    },
    setNewRootModal: (state, action) => {
      if (!action.payload.status) {
        state.newRootModal = false;
        state.editRootError = null;
        state.tryingEditRoot = false;
        state.rootName = "";
        state.rootLink = "";
        state.rootIcon = null;
        state.rootIconChanged = false;
        state.rootNameChanged = false;
        state.editRootId = null;
        state.newlyMadeRoot = null;
        state.linkPage = false;
        return;
      }
      state.newRootModal = true;
      if (action.payload.effice) {
        state.editRootId = action.payload.effice.id;
        state.rootIcon = action.payload.effice.picture_key;
        state.rootName = action.payload.effice.name;
      } else {
        state.editRootId = null;
      }
    },
  },
});

export const setPictureFile = (file) => async (dispatch) => {
  dispatch(setTryingEditRoot(true));
  if (file == null) {
    dispatch(setRootIconChanged(true));
    dispatch(setRootIcon(null));
    return;
  }
  //Perform validation (just checking size < constant)
  if (file.size > MAX_EFFICE_ICON_SIZE) {
    dispatch(setEditRootError("Image must be less than 6MB"));
    return;
  }
  try {
    window.efs_debug && window.log("Setting Picture File");
    window.efs_debug && window.log(file);
    const picture = await resizeFile(file);
    window.efs_debug && window.log(picture);
    dispatch(setRootIcon(picture));
    dispatch(setRootIconChanged(true));
  } catch (err) {
    window.efs_debug && window.log("Setting Picture File Error");
    window.efs_debug && window.log(err);
    dispatch(setEditRootError("Image not compatible"));
  }
};

export const newRootConfirm = () => async (dispatch, getState) => {
  if (getState().homeStart.rootName.toString().length > 100) {
    dispatch(setEditRootError("Root Name Too Long"));
    return;
  }
  dispatch(setEditRootError(null));
  dispatch(setTryingEditRoot(true));
  await dispatch(
    newRootMaker(getState().homeStart.rootName, getState().homeStart.rootIcon)
  );
  dispatch(setTryingEditRoot(false));
};

export const editRootIconConfirm = (effId) => async (dispatch, getState) => {
  return new Promise(async (res) => {
    dispatch(setTryingEditRoot(true));
    dispatch(setEditRootError(null));
    let rootIcon = getState().homeStart.rootIcon;
    if (!getState().homeStart.rootIconChanged) {
      await dispatch(setNewRootModal({ status: null }));
      res();
    } else if (rootIcon === null) {
      await dispatch(deleteEfficeIcon(effId))
        .then(() => {
          res();
        })
        .catch(() => {
          dispatch(setEditRootError("Could not change effice icon"));
        });
    } else if (typeof rootIcon === "object") {
      await dispatch(uploadEfficeIcon(effId, rootIcon))
        .then(() => {
          res();
        })
        .catch(() => {
          dispatch(setEditRootError("Could not change effice icon"));
        });
    } else {
      await dispatch(setNewRootModal({ status: null }));
      window.efs_debug && window.log("ERROR Edit Icon Confirm");
    }
    dispatch(setTryingEditRoot(false));
  });
};

export const {
  setRootName,
  setRootIcon,
  setRootIconChanged,
  setEditRootError,
  setTryingEditRoot,
  setNewlyMadeRoot,
  setLinkPage,
  setRootLink,
  setNewRootModal,
} = slice.actions;

export const selectRootName = (state) => state.homeStart.rootName;
export const selectEditRootError = (state) => state.homeStart.editRootError;
export const selectNewRootModal = (state) => state.homeStart.newRootModal;
export const selectEditRootId = (state) => state.homeStart.editRootId;
export const selectTryingEditRoot = (state) => state.homeStart.tryingEditRoot;
export const selectLinkPage = (state) => state.homeStart.linkPage;
export const selectNewlyMadeRoot = (state) => state.homeStart.newlyMadeRoot;
export const selectRootLink = (state) => state.homeStart.rootLink;
export const selectRootIcon = (state) => state.homeStart.rootIcon;
export const selectRootIconChanged = (state) => state.homeStart.rootIconChanged;
export const selectRootNameChanged = (state) => state.homeStart.rootNameChanged;

export default slice.reducer;

const resizeFile = (file) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      MAX_EFFICE_ICON_SIDE_LENGTH,
      MAX_EFFICE_ICON_SIDE_LENGTH,
      "JPEG",
      80,
      0,
      (uri) => {
        resolve(uri);
      },
      "blob",
      MIN_EFFICE_ICON_SIDE_LENGTH,
      MIN_EFFICE_ICON_SIDE_LENGTH
    );
  });
